import { Box, Button, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import type { Span } from "../../api/client";

function bySpanId(spans: Span[]): Map<string, Span> {
  const map = new Map<string, Span>();
  for (const s of spans) map.set(s.span_id, s);
  return map;
}

function childrenByParentId(spans: Span[]): Map<string | null, Span[]> {
  const map = new Map<string | null, Span[]>();
  for (const s of spans) {
    const key = s.parent_span_id ?? null;
    const arr = map.get(key) ?? [];
    arr.push(s);
    map.set(key, arr);
  }
  for (const [, arr] of map) {
    arr.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  }
  return map;
}

function pathToRoot(currentSpanId: string, spanById: Map<string, Span>): Span[] {
  const path: Span[] = [];
  let cur: Span | undefined = spanById.get(currentSpanId);
  while (cur) {
    path.push(cur);
    const parentId = cur.parent_span_id ?? null;
    if (!parentId) break;
    cur = spanById.get(parentId);
  }
  return path.reverse();
}

export interface SpanHierarchyProps {
  spans: Span[];
  currentSpanId: string;
  onNavigateSpan: (spanId: string) => void;
}

export function SpanHierarchy({ spans, currentSpanId, onNavigateSpan }: SpanHierarchyProps) {
  const spanById = useMemo(() => bySpanId(spans), [spans]);
  const childrenMap = useMemo(() => childrenByParentId(spans), [spans]);

  const chain = useMemo(() => pathToRoot(currentSpanId, spanById), [currentSpanId, spanById]);

  const current = spanById.get(currentSpanId);
  const children = childrenMap.get(currentSpanId) ?? [];

  if (!current) {
    return (
      <Typography variant="body2" sx={{ opacity: 0.7 }}>
        Span not found in trace.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box>
        <Typography variant="subtitle2">Path</Typography>
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {chain.map((s) => {
            const isCurrent = s.span_id === currentSpanId;
            return (
              <Stack key={s.span_id} direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="caption"
                  className="om-text-mono"
                  sx={{ opacity: 0.7, width: 90 }}
                >
                  {s.span_id.slice(0, 8)}…
                </Typography>
                {isCurrent ? (
                  <Typography variant="body2" fontWeight={700}>
                    {s.service_name} · {s.span_name}
                  </Typography>
                ) : (
                  <Button
                    size="small"
                    variant="text"
                    sx={{ px: 0.5, textTransform: "none", justifyContent: "flex-start" }}
                    onClick={() => onNavigateSpan(s.span_id)}
                  >
                    <Typography variant="body2">
                      {s.service_name} · {s.span_name}
                    </Typography>
                  </Button>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2">Children</Typography>
        {children.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
            No child spans.
          </Typography>
        ) : (
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {children.map((s) => (
              <Stack key={s.span_id} direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="caption"
                  className="om-text-mono"
                  sx={{ opacity: 0.7, width: 90 }}
                >
                  {s.span_id.slice(0, 8)}…
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  sx={{ px: 0.5, textTransform: "none", justifyContent: "flex-start" }}
                  onClick={() => onNavigateSpan(s.span_id)}
                >
                  <Typography variant="body2">
                    {s.service_name} · {s.span_name}
                  </Typography>
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
